import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles, Briefcase, ArrowRight, Upload, CheckCircle, AlertCircle, X, LogIn } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import usePageTitle from '../hooks/usePageTitle';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = '.pdf,.doc,.docx';

const Careers = () => {
  const { t } = useLanguage();
  const { user, isAuthenticated, isDevMode } = useAuth();
  usePageTitle('Careers');
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    position: '',
    employment_type: 'full_time',
    cover_letter: '',
    linkedin_url: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const positions = t('join.positions') || [];
  const advantages = t('join.advantages') || [];
  const typeOptions = t('careers.formTypeOptions') || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      setForm(prev => ({
        ...prev,
        full_name: user.username || '',
        email: user.email || '',
      }));
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const pos = searchParams.get('position');
    if (pos) {
      setForm(prev => ({ ...prev, position: pos }));
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setError('File exceeds 10MB limit');
      return;
    }
    setResumeFile(file);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.full_name || !form.email || !form.position) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!resumeFile) {
      setError('Please upload your resume.');
      return;
    }

    setSubmitting(true);
    try {
      if (isDevMode) {
        await new Promise(r => setTimeout(r, 1200));
      } else {
        const ext = resumeFile.name.split('.').pop();
        const storagePath = `${crypto.randomUUID()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(storagePath, resumeFile);
        if (uploadError) throw uploadError;

        const { error: insertError } = await supabase
          .from('applications')
          .insert({
            id: crypto.randomUUID(),
            full_name: form.full_name,
            email: form.email,
            phone: form.phone || null,
            position: form.position,
            employment_type: form.employment_type || null,
            resume_path: storagePath,
            cover_letter: form.cover_letter || null,
            linkedin_url: form.linkedin_url || null,
            status: 'submitted',
          });
        if (insertError) throw insertError;
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || t('careers.errorTitle'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      full_name: user?.username || '',
      email: user?.email || '',
      phone: '',
      position: '',
      employment_type: 'full_time',
      cover_letter: '',
      linkedin_url: '',
    });
    setResumeFile(null);
    setSubmitted(false);
    setError('');
  };

  if (submitted) {
    return (
      <div className="overflow-x-hidden">
        <section className="relative pt-28 pb-20 sm:pt-36 overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 mb-6">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t('careers.successTitle')}</h1>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-8">{t('careers.successMessage')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white font-semibold text-sm rounded-full hover:bg-accent-hover transition-colors"
              >
                {t('careers.successGoToDashboard')}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={resetForm}
                className="inline-flex items-center gap-2 px-6 py-3 border border-dark-border text-gray-300 font-semibold text-sm rounded-full hover:bg-white/5 transition-colors"
              >
                {t('careers.successApplyAnother')}
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="overflow-x-hidden">
      {/* Hero */}
      <section className="relative pt-28 pb-12 sm:pt-36 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh pointer-events-none" />
        <div className="absolute inset-0 bg-dot-pattern bg-dot opacity-30 pointer-events-none" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            {t('careers.heroTitle')}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
            {t('careers.heroTitle')}
          </h1>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            {t('careers.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Why Join */}
      {advantages.length > 0 && (
        <section className="py-10 sm:py-14">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-8 text-center">{t('careers.whyJoinTitle')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {advantages.map((adv, i) => (
                <div key={i} className="rounded-xl border border-dark-border bg-dark-card p-6 hover:border-accent/30 transition-colors duration-300">
                  <h3 className="text-white font-semibold text-base mb-2">{adv.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Open Positions */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-white mb-8">
            <Briefcase className="h-5 w-5 text-accent" />
            {t('careers.positionsTitle')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {positions.map((pos, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setForm(prev => ({ ...prev, position: pos.title }));
                  formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="group block w-full text-left border border-dark-border bg-dark-card rounded-xl px-5 py-4 hover:border-accent/40 hover:shadow-[0_0_24px_-4px_rgba(71,150,227,0.2)] hover:-translate-y-0.5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-dark"
              >
                <div className="font-semibold text-white text-base tracking-tight group-hover:text-accent transition-colors duration-200">
                  {pos.title}
                </div>
                <p className="text-gray-400 text-sm mt-1.5 leading-relaxed line-clamp-2">{pos.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  {pos.type}
                </span>
                <span className="mt-2 flex items-center gap-1 text-xs font-medium text-accent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {t('careers.applyNow')}
                  <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </button>
            ))}
          </div>
          <p className="text-center text-sm sm:text-base mt-6 font-bold text-google-yellow" style={{ textShadow: '0 0 20px rgba(251,188,4,0.5), 0 0 40px rgba(251,188,4,0.25)' }}>
            {t('careers.proposeRole')}
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-10 sm:py-14 scroll-mt-24" ref={formRef}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-8 text-center">{t('careers.formTitle')}</h2>

          {!isAuthenticated ? (
            <div className="rounded-xl border border-dark-border bg-dark-card p-8 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-accent/10 border border-accent/20 mb-5">
                <LogIn className="h-6 w-6 text-accent" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{t('careers.loginToApply')}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">{t('careers.loginToApplyDesc')}</p>
              <Link
                to="/login?redirect=/careers"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white font-semibold text-sm rounded-full hover:bg-accent-hover transition-colors"
              >
                {t('careers.loginToApply')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name (read-only, from account) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formName')}</label>
              <input
                type="text"
                value={form.full_name}
                readOnly
                className="w-full px-4 py-3 rounded-xl bg-dark-card/50 border border-dark-border text-gray-400 text-sm cursor-not-allowed"
              />
            </div>

            {/* Email (read-only, from account) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formEmail')}</label>
              <input
                type="email"
                value={form.email}
                readOnly
                className="w-full px-4 py-3 rounded-xl bg-dark-card/50 border border-dark-border text-gray-400 text-sm cursor-not-allowed"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formPhone')}</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t('careers.formPhonePlaceholder')}
                className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
              />
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formPosition')} *</label>
              <select
                name="position"
                value={form.position}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all appearance-none"
              >
                <option value="">{t('careers.formPosition')}...</option>
                {positions.map((p, i) => (
                  <option key={i} value={p.title}>{p.title}</option>
                ))}
                <option value="other">{t('careers.formPositionOther')}</option>
              </select>
            </div>

            {/* Employment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('careers.formType')}</label>
              <div className="flex flex-wrap gap-3">
                {Object.entries(typeOptions).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="employment_type"
                      value={key}
                      checked={form.employment_type === key}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <span className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ${
                      form.employment_type === key
                        ? 'bg-accent/10 border-accent/40 text-accent'
                        : 'bg-dark-card border-dark-border text-gray-400 hover:border-gray-500'
                    }`}>
                      {label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formResume')} *</label>
              <div className="relative">
                <input
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={handleFileChange}
                  className="sr-only"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className={`flex items-center gap-3 w-full px-4 py-4 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
                    resumeFile
                      ? 'border-accent/40 bg-accent/5'
                      : 'border-dark-border hover:border-gray-500 bg-dark-card'
                  }`}
                >
                  <Upload className={`h-5 w-5 flex-shrink-0 ${resumeFile ? 'text-accent' : 'text-gray-500'}`} />
                  <div className="min-w-0 flex-1">
                    {resumeFile ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-accent font-medium truncate">{resumeFile.name}</span>
                        <span className="text-xs text-gray-500">({(resumeFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">{t('careers.formResumeHint')}</span>
                    )}
                  </div>
                  {resumeFile && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setResumeFile(null); }}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </label>
              </div>
            </div>

            {/* Cover Letter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formCoverLetter')}</label>
              <textarea
                name="cover_letter"
                value={form.cover_letter}
                onChange={handleChange}
                placeholder={t('careers.formCoverLetterPlaceholder')}
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* LinkedIn */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">{t('careers.formLinkedIn')}</label>
              <input
                type="url"
                name="linkedin_url"
                value={form.linkedin_url}
                onChange={handleChange}
                placeholder={t('careers.formLinkedInPlaceholder')}
                className="w-full px-4 py-3 rounded-xl bg-dark-card border border-dark-border text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3.5 bg-accent text-white font-semibold text-sm rounded-xl hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('careers.formSubmitting')}
                </>
              ) : (
                <>
                  {t('careers.formSubmit')}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Careers;
